import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  showAllStrategies,
  showStrategyEditor,
  showPersonalStrategy,
  closeStrategyEditor,
  updateStategyDetails,
  saveNewStrategy,
  loadPersonalStrategiesFromNew,
  PORT_HUB_TYPES,
} from './../../../actions/data.js';
import styles from './StrategyList.module.css';
import i18n from './../../../i18n';
import StrategyRow from './StrategyRow.js';
import StrategyEditor from 'wt-react/dist/components/StrategyEditor/StrategyEditor';
import WTTabGroup from 'wt-react/dist/components/WTTabGroup/WTTabGroup';
import WTTable from 'wt-react/dist/components/WTTable/WTTable';
import WTText from 'wt-react/dist/components/WTText/WTText';
import WTSpinner from 'wt-react/dist/components/WTSpinner/WTSpinner';
import WTSearchBar from 'wt-react/dist/components/WTSearchBar/WTSearchBar';
import WTButton from 'wt-react/dist/components/WTButton/WTButton';
import { formatDate, getKeyMetrics } from './../../../common';

export const KEY_METRICS = ['performance', 'income', 'risk', 'sharpe', 'fees'];

// sort
const FILTER_LABELS = {
  'user-favorite': 'Subscribed Models',
  'managed-cio': 'Shared CIO Models',
  'user-custom-model': 'Models On Demand',
  'user-model': 'User Built Portfolios',
};

const SECTION_TITLES = {
  'user-favorite': 'My Dashboard',
  'managed-cio': 'Shared CIO Models',
  'user-custom-model': 'Models On Demand',
  'user-model': 'User Built Portfolios',
};

class StrategyList extends PureComponent {
  constructor(props) {
    super(props);
    this.strategyEditorRef = React.createRef();
    this.state = {
      menuIsOpen: false,
      savingNewStrategy: false,
      savingNewStrategyValue: null,
      userSelectedTab: false,
      didAutoSelectDefaultTab: false,
      filterType: ['user-favorite'],
      detailSearchQuery: '',
    };
  }

  componentDidUpdate(prevProps, prevState) {
    // Keep tooltips positioned after any layout changes
    ReactTooltip.hide();
    ReactTooltip.rebuild();

    const data = this.props.data || {};
    const prevData = prevProps.data || {};
    const strategyIdToShow = data.strategyIdToShow;
    const sectionsSummary = data.sectionsSummary || {};
    const prevSectionsSummary = prevData.sectionsSummary || {};

    const getSectionCount = (sections, type) => {
      const section = sections[type];
      return section && Array.isArray(section.data) ? section.data.length : 0;
    };

    const getFirstNonEmptyTab = (sections) => {
      const hasSubscribed = getSectionCount(sections, 'user-favorite') > 0;
      const hasSharedCio = getSectionCount(sections, 'managed-cio') > 0;
      const hasOnDemand = getSectionCount(sections, 'user-custom-model') > 0;
      const hasUserBuilt = getSectionCount(sections, 'user-model') > 0;

      if (hasSubscribed) return 'user-favorite';
      if (hasSharedCio) return 'managed-cio';
      if (hasOnDemand) return 'user-custom-model';
      if (hasUserBuilt) return 'user-model';
      // If nothing has data, fall back to User Built Portfolios (so the user can create one)
      return 'user-model';
    };

    // Auto-select the default tab after all 4 sections resolve, based on which tabs have data.
    // Priority rules:
    // - If Subscribed has data => Subscribed (user-favorite)
    // - Else if Shared CIO has data => Shared CIO (managed-cio)
    // - Else if Models on Demand has data => Models on Demand (user-custom-model)
    // - Else if User Built has data => User Built (user-model)
    // - Else (no data across all 4 tabs) => All (no filter)
    if (
      !this.state.didAutoSelectDefaultTab &&
      !this.state.userSelectedTab &&
      sectionsSummary.allResolved &&
      !prevSectionsSummary.allResolved
    ) {
      const sections = data.personalStrategiesSections || {};
      const desiredDefault = getFirstNonEmptyTab(sections);
      const desiredFilterType = desiredDefault ? [desiredDefault] : ['user-model'];

      const current = (this.state.filterType && this.state.filterType[0]) || null;
      const currentFilterType = this.state.filterType || [];
      const currentKey = current || null;
      if (
        (desiredDefault && currentKey !== desiredDefault) ||
        (!desiredDefault && currentFilterType.length > 0)
      ) {
        this.props.showAllStrategies();
        this.setMenuIsOpen(false);
        this.setState({
          filterType: desiredFilterType,
          currPage: 0,
          didAutoSelectDefaultTab: true,
        });
      } else {
        this.setState({ didAutoSelectDefaultTab: true });
      }
    }

    // If Subscribed or Shared CIO is selected but resolves empty, fall back to a tab with data (or All).
    if (sectionsSummary.allResolved) {
      const sections = data.personalStrategiesSections || {};
      const current = (this.state.filterType && this.state.filterType[0]) || null;
      const currentLower = String(current || '')
        .trim()
        .toLowerCase();
      const isHiddenWhenEmpty =
        currentLower === 'user-favorite' || currentLower === 'managed-cio';

      if (isHiddenWhenEmpty && getSectionCount(sections, currentLower) === 0) {
        const desiredDefault = getFirstNonEmptyTab(sections);
        const desiredFilterType = desiredDefault ? [desiredDefault] : ['user-model'];
        if (JSON.stringify(this.state.filterType || []) !== JSON.stringify(desiredFilterType)) {
          this.props.showAllStrategies();
          this.setMenuIsOpen(false);
          this.setState({ filterType: desiredFilterType, currPage: 0 });
        }
      }
    }

    // Open from URL once strategies are available
    if (data.personalStrategies !== prevData.personalStrategies) {
      const ps = data.personalStrategies;
      if (Array.isArray(ps) && ps.length > 0) {
        this.readURL();
      }
    }

    // When closing the viewer, drop id from hash
    if (
      prevData.strategyIdToShow !== strategyIdToShow &&
      (strategyIdToShow === false ||
        strategyIdToShow === null ||
        strategyIdToShow === undefined)
    ) {
      const history = this.props.history;
      const hash = (history.location.hash || '').replace(/^#/, '');

      if (hash) {
        // split the hash into parts separated by &
        const parts = hash.split('&');

        // keep only parts that DON'T start with id= or myportfolioid=
        const cleaned = parts.filter(function (p) {
          const startsWithId = p.startsWith('id=');
          const startsWithMyPortfolioId = p.startsWith('myportfolioid=');
          return !startsWithId && !startsWithMyPortfolioId;
        });

        // stitch it back together and update the URL
        const newHash = cleaned.join('&');
        history.replace({ hash: newHash });
      }
    }

  }

  readURL = () => {
    const history = this.props.history;
    const hash = (history.location.hash || '').replace(/^#/, '');
    if (!hash) return;

    // look for id=… or myportfolioid=…
    const parts = hash.split('&');
    const idParam = parts.find(
      (p) => /^id=/.test(p) || /^myportfolioid=/.test(p)
    );
    if (!idParam) return;

    const id = idParam.split('=')[1];
    if (!id) return;

    const data = this.props.data || {};
    const personalStrategies = Array.isArray(data.personalStrategies)
      ? data.personalStrategies
      : [];

    // Only auto-open once we have a concrete match from one of the loaded sections.
    // If there is no match yet, keep waiting for the remaining section loads.
    const match = personalStrategies.find((strategy) => strategy?.id === id);
    if (!match) return;

    const rawType = match.portHubType;
    const types = Array.isArray(rawType) ? rawType : rawType ? [rawType] : [];
    const normalizedTypes = types
      .map((t) =>
        String(t || '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
    const matchedTab =
      normalizedTypes.find((t) => PORT_HUB_TYPES.includes(t)) ||
      normalizedTypes[0];

    if (matchedTab && this.state.filterType?.[0] !== matchedTab) {
      this.setState({
        filterType: [matchedTab],
        currPage: 0,
        userSelectedTab: true,
      });
    }

    if (data.strategyIdToShow !== id) {
      this.props.showPersonalStrategy(id);
    }

    // Once matched and opened, strip id from hash so refresh does not re-open it.
    const cleaned = parts.filter((p) => {
      return !/^id=/.test(p) && !/^myportfolioid=/.test(p);
    });
    const newHash = cleaned.join('&');
    try {
      history.replace({ hash: newHash });
    } catch (_) {}
  };

  setMenuIsOpen = (menuIsOpen) => this.setState({ menuIsOpen });

  // --- Single-select filter buttons (radio-like) ---
  toggleFilter = (value) => {
    // 1) Close any open details
    this.props.showAllStrategies();

    // collapse any row-level menus
    this.setMenuIsOpen(false);

    // new filter and reset paging
    this.setState((prev) => {
      // Keep radio-like behavior: clicking the active tab should not clear the filter.
      return { filterType: [value], currPage: 0, userSelectedTab: true };
    });
  };

  clearFilter = () => {
    this.setState({ filterType: [], currPage: 0, userSelectedTab: true });
  };

  getSectionState = (value) => {
    if (!value) return null;
    const data = this.props.data || {};
    const sections = data.personalStrategiesSections || {};
    return sections[value] || null;
  };

  getSectionStatus = (value) => {
    const section = this.getSectionState(value);
    return section && section.status ? section.status : 'idle';
  };

  getSectionError = (value) => {
    const section = this.getSectionState(value);
    return section && section.error ? section.error : null;
  };

  setSavingStrategyInProgress = (value, isSaving) => {
    const data = this.props.data || {};
    const personalStrategies = data.personalStrategies || [];
    const updated = [value].concat(personalStrategies);

    if (!isSaving && value) {
      this.props.loadPersonalStrategiesFromNew(updated);
    }
    this.setState({
      savingNewStrategy: isSaving,
      savingNewStrategyValue: value,
    });
  };

  // Helper: pretty label using FILTER_LABELS map
  getPrettyLabel = (value) => {
    if (value == null) return '';
    const key = String(value).trim();
    return FILTER_LABELS[key] || key;
  };

  // --- Build unique type options from portHubType in data ---
  getTypeOptionsFromData = () => {
    const { data = {} } = this.props;
    const personalStrategies = data.personalStrategies || [];
    const sections = data.personalStrategiesSections || {};
    const seenLower = new Set();
    const options = [];

    const shouldShowType = (value) => {
      const key = String(value || '')
        .trim()
        .toLowerCase();

      // Hide Subscribed + Shared CIO tabs when we know they have no data.
      if (key === 'user-favorite' || key === 'managed-cio') {
        const section = sections[key];
        const status = section && section.status ? section.status : 'idle';
        if (status !== 'success') return true; // still loading / error / idle
        const count = section && Array.isArray(section.data) ? section.data.length : 0;
        return count > 0;
      }

      return true;
    };

    const addOption = (value) => {
      if (!value) return;
      const label = String(value).trim();
      if (!label) return;
      if (!shouldShowType(label)) return;
      const lower = label.toLowerCase();
      if (seenLower.has(lower)) return;
      seenLower.add(lower);
      options.push({ value: label, label });
    };

    // Always show the primary tabs in the expected order
    (PORT_HUB_TYPES || []).forEach(addOption);

    // Collect unique types from all personal strategies
    personalStrategies.forEach((strategy = {}) => {
      const raw = strategy.portHubType;
      if (!raw) return;
      const types = Array.isArray(raw) ? raw : [raw];
      types.forEach(addOption);
    });

    // Preserve your preferred order first, then alphabetical fallback
    const preferredOrder = [
      'user-favorite',
      'managed-cio',
      'user-custom-model',
      'user-model',
      ...(PORT_HUB_TYPES || []),
    ];

    const rank = (val) => {
      const i = preferredOrder.indexOf(String(val).toLowerCase());
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };

    options.sort((a, b) => {
      const ra = rank(a.value);
      const rb = rank(b.value);
      if (ra !== rb) return ra - rb;
      const la = FILTER_LABELS[a.value] || a.value;
      const lb = FILTER_LABELS[b.value] || b.value;
      return la.localeCompare(lb);
    });

    return options;
  };

  // --- Filtering (by name + portHubType) ---
  getFilteredStrategies = () => {
    const { data = {} } = this.props;
    const personalStrategies = Array.isArray(data.personalStrategies)
      ? data.personalStrategies
      : [];
    const searchTerm = (this.state.searchTerm || '').trim().toLowerCase();
    const filterType = this.state.filterType || [];

    const selectedLower = new Set(
      filterType
        .map((v) =>
          String(v || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );

    const noTypeFilter = selectedLower.size === 0;

    const filtered = personalStrategies.filter((strategy = {}) => {
      const name = (strategy.name || '').toLowerCase();
      const nameMatches = searchTerm === '' || name.includes(searchTerm);

      const raw = strategy.portHubType;
      if (noTypeFilter) return nameMatches;

      const typeArray = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const typeMatches = typeArray.some((t) =>
        selectedLower.has(
          String(t || '')
            .trim()
            .toLowerCase()
        )
      );

      return nameMatches && typeMatches;
    });

    // Cache debug key
    const logKey = JSON.stringify({ needle: searchTerm, filterType });
    if (this._lastFilterLogKey !== logKey) {
      this._lastFilterLogKey = logKey;
    }

    return filtered;
  };

  // --- Asset-class breakdown string (mirrors StrategyRow logic) ---
  buildAssetBreakdownString = (ps) => {
    const raw = ps?.assetClassTotals && typeof ps.assetClassTotals === 'object' ? ps.assetClassTotals : {};
    const toPct = (x) => Math.round(Number(x || 0) * 100);
    const equity = toPct(raw.Equity ?? 0);
    const fixed  = toPct(raw['Fixed Income'] ?? raw.FixedIncome ?? 0);
    const alts   = toPct(raw.Alternatives ?? raw.Alternative ?? 0);
    const parts = [
      { label: 'EQ', val: equity, ord: 0 },
      { label: 'Fxd Inc', val: fixed, ord: 1 },
      { label: 'Alts', val: alts, ord: 2 },
    ].filter(p => p.val > 0).sort((a, b) => b.val - a.val || a.ord - b.ord);
    return parts.length ? parts.map(p => `${p.label} ${p.val}%`).join(' | ') : '-';
  };

  hasPortHubType = (ps, target) => {
    if (!ps) return false;
    const raw = ps.portHubType;
    const match = (v) => String(v).trim().toUpperCase() === target;
    return Array.isArray(raw) ? raw.some(match) : match(raw);
  };

  // --- Build WTTable headers for current tab ---
  buildTableHeaders = (isBreakdownView) => {
    if (isBreakdownView) {
      return ['', 'Name', 'Inception Date', 'Performance Since inception', 'Income', 'Asset Class', 'Expense Ratio'];
    }
    return ['', 'Name', 'Inception Date', 'Performance Since inception', 'Income', 'Risk', 'Risk Adj. Returns', 'Expense Ratio'];
  };

  // --- Build WTTable 2-D data array from strategies ---
  buildTableData = (strategies, isBreakdownView) => {
    return strategies.map((strategy) => {
      const strategyTypeIsCustom = !!strategy.customUserModel;
      const isUserFavorite = this.hasPortHubType(strategy, 'USER-FAVORITE');
      const isManagedCio   = this.hasPortHubType(strategy, 'MANAGED-CIO');
      const useBreakdown   = isBreakdownView || isManagedCio || isUserFavorite;

      const metricsValues  = strategy.statistics ? getKeyMetrics(strategy.statistics) : {};

      const inceptionDate = formatDate(
        strategyTypeIsCustom
          ? strategy.createDate || strategy.inceptionDate || strategy.commonPeriodStart
          : strategy.inceptionDate || strategy.commonPeriodStart
      ) || '-';

      const fmt = (val, suffix) => (val && val !== '-' ? `${val}${suffix}` : '-');

      const openStrategy = () => this.props.showPersonalStrategy(strategy.id);
      const handleAnalyseKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openStrategy();
        }
      };
      const analyseCell = {
        type: 'custom',
        render: () => (
          <span
            className={styles['psl-inline-action']}
            onClick={openStrategy}
            onKeyDown={handleAnalyseKeyDown}
            role="button"
            tabIndex={0}
          >
            Analyse <span className={styles['psl-inline-action-arrow']}>→</span>
          </span>
        ),
      };
      const nameCell    = { type: 'link',   text: strategy.name, onClick: openStrategy };

      if (useBreakdown) {
        return [
          analyseCell,
          nameCell,
          inceptionDate,
          fmt(metricsValues.performance, '%'),
          fmt(metricsValues.income, '%'),
          this.buildAssetBreakdownString(strategy),
          fmt(metricsValues.fees, ' bps'),
        ];
      }
      return [
        analyseCell,
        nameCell,
        inceptionDate,
        fmt(metricsValues.performance, '%'),
        fmt(metricsValues.income, '%'),
        fmt(metricsValues.risk, '%'),
        metricsValues.sharpe || '-',
        fmt(metricsValues.fees, ' bps'),
      ];
    });
  };

  renderDetailSearch() {
    const { data = {} } = this.props;
    const strategies = Array.isArray(data.personalStrategies) ? data.personalStrategies : [];
    const query = this.state.detailSearchQuery || '';

    const matches = query.trim()
      ? strategies.filter(s => s.name && s.name.toLowerCase().includes(query.toLowerCase()))
      : [];

    const results = matches.length > 0
      ? [{ group: 'Portfolios', items: matches.slice(0, 8).map(s => s.name) }]
      : undefined;

    const handleClear = () => this.setState({ detailSearchQuery: '' });

    return (
      <div className={styles['psl-detail-search']}>
        <WTSearchBar
          placeholder="Search by Portfolio name"
          value={query}
          onChange={(val) => this.setState({ detailSearchQuery: val })}
          onClear={handleClear}
          results={results}
          onResultClick={(name) => {
            const strategy = strategies.find(s => s.name === name);
            if (strategy) {
              this.setState({ detailSearchQuery: '' });
              this.props.showPersonalStrategy(strategy.id);
            }
          }}
          height={43}
          borderRadius={999}
        />
        <WTButton variant="primary-blue" onClick={handleClear}>
          Clear Search
        </WTButton>
      </div>
    );
  }

  // --- Tab filter bar only (WTTable provides its own search) ---
  renderTabBar(color = 'blue') {
    const { filterType = [] } = this.state;
    const typeOptions = this.getTypeOptionsFromData();
    const tabButtons = typeOptions.map((opt) => ({
      value: opt.value,
      label: this.getPrettyLabel(opt.value),
    }));
    const activeTab = (filterType && filterType[0]) || null;

    return (
      <div className={styles['psl-filter-row']}>
        <WTTabGroup
          buttons={tabButtons}
          active={activeTab}
          onChange={(value) => this.toggleFilter(value)}
          color={color}
          variant="button"
          underline={true}
          underlineFull={true}
        />
      </div>
    );
  }

  renderTabLoadingState(label) {
    return (
      <div className={styles['psl-center-spinner']}>
        <WTSpinner size="large" color="navy" />
      </div>
    );
  }

  renderTabErrorState(label, message) {
    return (
      <div className={styles['psl-center-spinner']}>
        <FontAwesomeIcon icon="exclamation-triangle" size="2x" color="#D22630" />
        <WTText variant="body2" color="danger" className={styles['psl-error-message']}>
          {message || 'Unable to load'}
        </WTText>
      </div>
    );
  }

  handleGoToModelCenter = () => {
    window.location.assign('/portfolio-solutions/model-portfolios');
  };

  renderTabEmptyState(activeFilter) {
    if (activeFilter === 'user-favorite') {
      return (
        <div className={styles['psl-empty-state']}>
          <div className="psl-empty-state-label">
            <h4>
              No Data. Click to Visit our{' '}
              <button
                type="button"
                className={styles['psl-model-center-link']}
                onClick={this.handleGoToModelCenter}
              >
                Model Center
              </button>
              .
            </h4>
          </div>
        </div>
      );
    }

    return (
      <div className={styles['psl-empty-state']}>
        <div className="psl-empty-state-label">
          <h4>No data available for display.</h4>
        </div>
      </div>
    );
  }

  render() {
    const strategiesLoadError = this.props.strategiesLoadError;
    const data = this.props.data || {};
    const params = this.props.params || {};
    const sectionsSummary = data.sectionsSummary || {};
    const sections = data.personalStrategiesSections || {};
    const hasAnyCompletion = !!sectionsSummary.hasAnyCompletion;

    if (strategiesLoadError) {
      return (
        <div className={styles['psl-empty-state']}>
          <div className="psl-empty-state-label">
            <h4>
              {i18n.t('error-loading-strategies') || 'Error Loading Portfolios'}
            </h4>
          </div>
        </div>
      );
    }

    const personalStrategies = data.personalStrategies;
    if (!hasAnyCompletion || personalStrategies == null) {
      return (
        <div className={styles['psl-loading-state']}>
          <WTSpinner size="large" color="navy" />
        </div>
      );
    }

    const filterType = this.state.filterType || [];
    const activeFilter = filterType.length > 0 ? filterType[0] : null;
    const activeSection = activeFilter ? sections[activeFilter] || {} : null;
    const activeStatus = activeSection ? activeSection.status : null;
    const activeError = activeSection ? activeSection.error : null;
    const shouldShowFilters = !this.state.menuIsOpen;

    const strategyIdToShow = data.strategyIdToShow;
    const strategySaveInProgress = data.strategySaveInProgress;
    const haveList =
      Array.isArray(personalStrategies) && personalStrategies.length > 0;

    const headings = !this.state.menuIsOpen ? (
      <div className={styles['psl-heading-block']}>
        <WTText variant="h2" as="h2" color="navy" className={styles['psl-heading-title']}>
          {i18n.t('title-heading')}
        </WTText>
        <WTText variant="body" color="navy" className={styles['psl-heading-subtitle']}>
          {i18n.t('title-subheading')}
        </WTText>
      </div>
    ) : null;

    const shouldShowNoStrategies =
      sectionsSummary.allSuccessEmpty &&
      !this.state.savingNewStrategy &&
      !haveList;

    const noDataForTab =
      activeStatus === 'success' &&
      !this.state.savingNewStrategy &&
      this.getFilteredStrategies().length === 0;

    if (shouldShowNoStrategies) {
      return (
        <div className={styles['psl-empty-state']}>
          <div className="psl-empty-state-label">
            <h4>{i18n.t('no-strategies')}</h4>
          </div>
          <div className={styles['psl-new-strategy']}>
            <StrategyEditor
              key={'new-strategy-editor-1'}
              type={'create'}
              buttonLabel="+ Add New Portfolio"
              showEditor={true}
              token={params.token}
              clientEmail={params.clientEmail}
              dataspanApiUrl={process.env.REACT_APP_API_URL || ''}
              benchmarks={[]}
              checkTickersUrl={process.env.REACT_APP_DPD_URL || ''}
              typeHeadPlaceholder={'Search By Ticker or Name'}
              showInfoModal={true}
              closeModalOnSave={true}
              savedStrategyCallBack={(value, isSaving) =>
                this.setSavingStrategyInProgress(value, isSaving)
              }
            />
          </div>
        </div>
      );
    }

    const prettyFilterLabel = this.getPrettyLabel(activeFilter);

    const ft = (filterType || []).map((v) => String(v).toLowerCase());
    const isBreakdownView =
      ft.length === 1 && (ft[0] === 'managed-cio' || ft[0] === 'user-favorite');

    if (activeFilter && activeStatus === 'loading') {
      return (
        <div className={styles.strategylistPS}>
          {headings}
          {shouldShowFilters && this.renderTabBar()}
          {this.renderTabLoadingState(prettyFilterLabel)}
        </div>
      );
    }

    if (activeFilter && activeStatus === 'error') {
      return (
        <div className={styles.strategylistPS}>
          {headings}
          {shouldShowFilters && this.renderTabBar('danger')}
          {this.renderTabErrorState(prettyFilterLabel, activeError)}
        </div>
      );
    }

    if (activeFilter && noDataForTab) {
      return (
        <div className={styles.strategylistPS}>
          {headings}
          {shouldShowFilters && this.renderTabBar()}
          {this.renderTabEmptyState(activeFilter)}
        </div>
      );
    }

    // Expanded single-strategy view
    const strategiesList = Array.isArray(personalStrategies) ? personalStrategies : [];
    const isExpanded = strategyIdToShow != null;
    const expanded = isExpanded
      ? strategiesList.find((s) => s?.id === strategyIdToShow) || null
      : null;

    if (isExpanded && expanded) {
      return (
        <div className={`${styles.strategylistPS}${isBreakdownView ? ` ${styles['strategylistPS--breakdown-view']}` : ''}`}>
          {headings}
          {shouldShowFilters && this.renderTabBar()}
          {this.renderDetailSearch()}
          <div className={styles['psl-table-scroll']}>
            <div className={styles['psl-table-canvas']}>
              <StrategyRow
                key={expanded.id}
                personalStrategy={expanded}
                openMenu
                striped={false}
                breakdownView={isBreakdownView}
                saveInProgress={!!strategySaveInProgress && strategySaveInProgress === expanded.id}
                menuIsOpen={this.state.menuIsOpen}
                setMenuIsOpen={this.setMenuIsOpen}
              />
            </div>
          </div>
        </div>
      );
    }

    if (this.state.savingNewStrategy || haveList) {
      const filteredStrategies = this.getFilteredStrategies();
      const tableHeaders = this.buildTableHeaders(isBreakdownView);
      const tableData = this.buildTableData(filteredStrategies, isBreakdownView);

      return (
        <div className={`${styles.strategylistPS}${isBreakdownView ? ` ${styles['strategylistPS--breakdown-view']}` : ''}`}>
          {headings}
          {shouldShowFilters && this.renderTabBar()}

          <WTTable
            className={styles['psl-table']}
            tableClassName="psl-table__table"
            title={SECTION_TITLES[activeFilter] || prettyFilterLabel}
            titleColor="#050139"
            titleSize={18}
            headers={tableHeaders}
            data={tableData}
            pageSize={10}
            searchable={true}
            searchPlaceholder="Search by Portfolio name"
            searchHeight={43}
            searchRadius={999}
            searchClearVariant="primary-blue"
            showRowLines={true}
            headerAlign="left"
            bodyAlign="left"
            footerAction={{
              text: '+ Add New Portfolio',
              onClick: () => this.strategyEditorRef.current?.querySelector('button')?.click(),
            }}
            footerActionVariant="text"
          />

          {/* Hidden StrategyEditor triggered by the WTTextButton above */}
          <div
            ref={this.strategyEditorRef}
            style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
          >
            <StrategyEditor
              key="new-strategy-editor-hidden"
              type="create"
              buttonLabel="+"
              showEditor
              token={params.token}
              clientEmail={params.clientEmail}
              dataspanApiUrl={process.env.REACT_APP_API_URL || ''}
              benchmarks={[]}
              checkTickersUrl={process.env.REACT_APP_DPD_URL || ''}
              typeHeadPlaceholder="Search By Ticker or Name"
              savedStrategyCallBack={(value, isSaving) =>
                this.setSavingStrategyInProgress(value, isSaving)
              }
              closeModalOnSave
              showInfoModal={false}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={styles['psl-loading-state']}>
        <WTSpinner size="large" color="navy" />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    data: state.data,
    params: state.params,
    strategiesLoadError: state.data.strategiesLoadError,
  };
}

export default withRouter(
  connect(mapStateToProps, {
    showAllStrategies,
    showStrategyEditor,
    showPersonalStrategy,
    closeStrategyEditor,
    updateStategyDetails,
    saveNewStrategy,
    loadPersonalStrategiesFromNew,
  })(StrategyList)
);
